import { useEffect, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PROJECTS } from "../data/content";
import { scrollStore } from "../lib/scroll";
import { EASE, FadeIn, MaskedLine, Eyebrow } from "../components/sora/Reveal";

const RevealImage = ({ src, alt, className }) => (
  <div className="overflow-hidden">
    <motion.img
      src={src}
      alt={alt}
      loading="lazy"
      initial={{ scale: 1.14 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 2, ease: EASE }}
      className={`w-full object-cover ${className}`}
    />
  </div>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const index = PROJECTS.findIndex((p) => p.slug === slug);
  const project = PROJECTS[index];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  useEffect(() => {
    scrollStore.scrollTo(0, { immediate: true });
  }, [slug]);

  if (!project) return <Navigate to="/" replace />;

  const next = PROJECTS[(index + 1) % PROJECTS.length];
  const meta = [
    ["Location", project.location],
    ["Event Type", project.typeLabel],
    ["Year", project.year],
    ["Guests", project.guests],
    ["Services", project.services],
  ];

  return (
    <main key={project.slug} data-testid="case-study-page">
      <section ref={ref} className="relative h-[88vh] overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <motion.img
            src={project.img.replace("w=1200", "w=2200")}
            alt={project.name}
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.4, ease: EASE }}
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#2e2f1e]/40 via-[#2e2f1e]/10 to-[#2e2f1e]/65" />
        <button
          onClick={() => navigate("/", { state: { scrollTo: "#portfolio" } })}
          data-testid="case-study-back-button"
          className="absolute left-6 top-28 z-10 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-ivory/80 transition-colors duration-500 hover:text-ivory sm:left-10 lg:left-16"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All Works
        </button>
        <div className="relative z-10 flex h-full flex-col justify-end px-6 pb-24 sm:px-10 lg:px-16">
          <FadeIn>
            <p className="text-[10px] font-medium uppercase tracking-[0.45em] text-ivory/85">
              {project.location} — {project.typeLabel}
            </p>
          </FadeIn>
          <h1
            data-testid="case-study-title"
            className="mt-6 font-serif font-light italic leading-[1.02] text-ivory text-[clamp(2.8rem,8vw,7.5rem)]"
          >
            <MaskedLine delay={0.2}>{project.name}</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1600px] gap-16 px-6 py-28 sm:px-10 sm:py-36 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-4">
          <FadeIn>
            <dl className="space-y-7 border-t border-line pt-8">
              {meta.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[10px] font-medium uppercase tracking-[0.35em] text-olive">
                    {k}
                  </dt>
                  <dd className="mt-2 font-serif text-lg font-light text-ink">
                    {v}
                  </dd>
                </div>
              ))}
            </dl>
          </FadeIn>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <FadeIn>
            <Eyebrow>The Brief</Eyebrow>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-8 font-serif text-2xl font-light leading-relaxed text-ink sm:text-[2rem]">
              {project.story1}
            </p>
          </FadeIn>
          {project.story2 && (
            <FadeIn delay={0.2}>
              <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ink/75">
                {project.story2}
              </p>
            </FadeIn>
          )}
          {project.approach && (
            <>
              <FadeIn delay={0.15} className="mt-16">
                <Eyebrow>Our Approach</Eyebrow>
              </FadeIn>
              <FadeIn delay={0.25}>
                <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ink/75">
                  {project.approach}
                </p>
              </FadeIn>
            </>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 pb-32 sm:px-10 lg:px-16">
        <FadeIn>
          <RevealImage
            src={project.gallery[0]}
            alt={`${project.name} — detail`}
            className="aspect-[16/9]"
          />
        </FadeIn>
        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <FadeIn
            delay={0.15}
            className="flex items-end lg:col-span-4 lg:col-start-1"
          >
            <p className="max-w-xs font-serif text-xl font-light italic leading-relaxed text-olive">
              {project.detail}
            </p>
          </FadeIn>
          <FadeIn delay={0.1} className="lg:col-span-5 lg:col-start-8">
            <RevealImage
              src={project.gallery[1]}
              alt={`${project.name} — atmosphere`}
              className="aspect-[3/4]"
            />
          </FadeIn>
        </div>
      </section>

      <section className="bg-sand py-28 sm:py-36">
        <div className="mx-auto max-w-[1600px] px-6 text-center sm:px-10">
          <FadeIn>
            <div className="flex justify-center">
              <Eyebrow>Next Project</Eyebrow>
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <button
              onClick={() => navigate(`/work/${next.slug}`)}
              data-testid="case-study-next-link"
              className="group mt-8 inline-flex items-center gap-6"
            >
              <span className="font-serif text-4xl font-light italic text-ink transition-colors duration-500 group-hover:text-olive sm:text-6xl">
                {next.name}
              </span>
              <ArrowRight
                size={28}
                strokeWidth={1.25}
                className="text-olive transition-transform duration-500 group-hover:translate-x-2"
              />
            </button>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
